// @flow
/** @jsx jsx */

import { type Node, useEffect, useRef, useState } from 'react';

import { jsx } from '@emotion/core';
import { Button, GridLayout } from '@zapier/common-components';
import { isEmptyString, parseSeparatedList } from '@zapier/common-utils';

import EmailInviterErrorBlock from './EmailInviterErrorBlock';
import EmailInviterInput from './EmailInviterInput';

import {
  type EmailInviterInviteProp,
  type EmailMessages,
} from '@zapier/team/src/types';

type Props = {
  canInvite: boolean,
  ConfirmEmail: () => Node,
  emailErrors: EmailMessages,
  emailSuccess: EmailMessages,
  isInviting: boolean,
  onInvite: EmailInviterInviteProp,
  roles: Array<string>,
};

const EmailInviter = (props: Props) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const inputRef = useRef();

  useEffect(() => {
    if (!props.isInviting && inputRef.current) {
      // $FlowFixMe - flow doesn't understand useRef
      inputRef.current.focus();
    }
  }, [props.isInviting]);

  useEffect(() => {
    // If there are any invalid emails sent back from the server, overwrite
    // the form data to contain the invalid email string
    const emailString = props.emailErrors.map(e => e.email).join(', ');
    if (emailString) {
      setEmail(emailString);
    }
  }, [props.emailErrors]);

  const onSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Parse the emails before sending them off to create entity. This will
    // split the emails by comma, semi-colon, tabs, and new lines.
    const emailList = parseSeparatedList(email);

    // Clear text input
    setEmail('');

    props.onInvite(emailList, role);
  };

  return (
    <GridLayout gap={20}>
      {props.canInvite ? (
        <form onSubmit={onSubmit}>
          <GridLayout
            columns="1fr 120px"
            gap={20}
            propsQueries={{
              phoneOnly: {
                columns: null,
              },
            }}
          >
            <EmailInviterInput
              inputRef={inputRef}
              isDisabled={props.isInviting}
              onChange={e => setEmail(e.target.value)}
              onRoleChanged={setRole}
              placeholder="Team member's email address..."
              roles={props.roles}
              selectedRole={role}
              value={email}
            />

            <Button
              color="primary"
              isDisabled={isEmptyString(email) || !role}
              isWorking={props.isInviting}
            >
              Invite
            </Button>
          </GridLayout>
        </form>
      ) : props.ConfirmEmail ? (
        <props.ConfirmEmail additionalContext="before you can invite team members." />
      ) : null}
      <EmailInviterErrorBlock
        emailErrors={props.emailErrors}
        emailSuccess={props.emailSuccess}
      />
    </GridLayout>
  );
};

export default EmailInviter;
